import React from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Fund, PipelineStage } from '../../types/fundraise';
import KanbanColumn from './KanbanColumn';
import FundCard from './FundCard';

interface KanbanBoardProps {
  stages: PipelineStage[];
  funds: Fund[];
  onMoveFund: (fundId: string, newStage: string, position: number) => void;
  onDeleteFund?: (fund: Fund) => void;
  onFundClick?: (fund: Fund) => void;
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({
  stages,
  funds,
  onMoveFund,
  onDeleteFund,
  onFundClick,
}) => {
  const [activeFund, setActiveFund] = React.useState<Fund | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const fund = funds.find((f) => f.id === active.id);
    if (fund) {
      setActiveFund(fund);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveFund(null);

    if (!over) return;

    const fundId = active.id as string;
    const targetStageId = over.id as string;

    // Get the fund being moved
    const fund = funds.find((f) => f.id === fundId);
    if (!fund) return;

    // If dropping on the same stage, don't do anything
    if (fund.stage === targetStageId) return;

    // Get the funds in the target stage to determine position
    const targetStageFunds = funds.filter((f) => f.stage === targetStageId);
    const newPosition = targetStageFunds.length;

    onMoveFund(fundId, targetStageId, newPosition);
  };

  // Group funds by stage
  const fundsByStage = React.useMemo(() => {
    const grouped: Record<string, Fund[]> = {};
    stages.forEach((stage) => {
      grouped[stage.id] = [];
    });
    funds.forEach((fund) => {
      if (grouped[fund.stage]) {
        grouped[fund.stage].push(fund);
      }
    });
    // Sort by position within each stage
    Object.keys(grouped).forEach((stageId) => {
      grouped[stageId].sort((a, b) => a.position - b.position);
    });
    return grouped;
  }, [stages, funds]);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="su-kanban-board">
        {stages.map((stage) => (
          <KanbanColumn
            key={stage.id}
            stage={stage}
            funds={fundsByStage[stage.id] || []}
            onDeleteFund={onDeleteFund}
            onFundClick={onFundClick}
          />
        ))}
      </div>
      <DragOverlay>
        {activeFund ? <FundCard fund={activeFund} /> : null}
      </DragOverlay>
    </DndContext>
  );
};

export default KanbanBoard;
